// Per-frame VRM life: blinking, voice-driven mouth, eye look-at, spring bones
// (hair/clothes) and first-person head hiding. Uses only the VRM instance's
// own methods, so this module adds no three-vrm code to the main bundle.
import * as THREE from 'three';
import type { VRM } from '@pixiv/three-vrm';

const VOWELS = ['aa', 'oh', 'ih', 'ee', 'ou'] as const;
// How often each vowel is picked while talking (aa/oh read best at a distance).
const VOWEL_WEIGHTS = [0.4, 0.22, 0.16, 0.12, 0.1];

const _hips = new THREE.Vector3();

export class VrmFace {
  private elapsed = 0;
  private nextBlink: number;
  private blinkStart = -1;
  private mouth = 0;
  private vowel = 0;
  private nextSyllable = 0;
  private vowelWeights = [0, 0, 0, 0, 0];
  private seed: number;
  private lastHips = new THREE.Vector3();
  private needsSpringReset = true;
  private hasMouth: boolean;
  private hasBlink: boolean;

  constructor(private vrm: VRM, seedText: string) {
    let h = 2166136261;
    for (const c of seedText) h = Math.imul(h ^ c.charCodeAt(0), 16777619);
    this.seed = h >>> 0;
    this.nextBlink = 1 + this.random() * 3;
    const em = vrm.expressionManager;
    this.hasBlink = !!em?.getExpression('blink');
    this.hasMouth = !!em?.getExpression('aa');
    if (vrm.lookAt) vrm.lookAt.autoUpdate = false;
  }

  private random() {
    this.seed = (Math.imul(this.seed, 1664525) + 1013904223) >>> 0;
    return this.seed / 4294967296;
  }

  /** Current mouth opening 0..1 (for tests/diagnostics). */
  get mouthOpen() { return this.mouth; }

  /** Snap spring bones to rest next frame (after a teleport/spawn). */
  resetSprings() { this.needsSpringReset = true; }

  /**
   * @param voice  0..1 speech energy (LiveKit audio level); 0 when silent/muted
   * @param lookTarget world position to look at, or null to look ahead
   */
  update(delta: number, voice: number, lookTarget: THREE.Vector3 | null) {
    const dt = Math.max(0, Math.min(1 / 20, delta));
    this.elapsed += dt;
    const em = this.vrm.expressionManager;

    // --- Blink: every 2–6 s, ~150 ms, occasionally a double blink ---
    if (this.elapsed >= this.nextBlink) {
      this.blinkStart = this.elapsed;
      this.nextBlink = this.elapsed + (this.random() < 0.12 ? 0.35 : 2 + this.random() * 4);
    }
    const phase = (this.elapsed - this.blinkStart) / 0.15;
    const blink = this.blinkStart >= 0 && phase >= 0 && phase <= 1 ? Math.sin(phase * Math.PI) : 0;
    if (em && this.hasBlink) em.setValue('blink', blink);

    // --- Lip sync from audio level: open/close with energy, vowel shape per syllable ---
    const level = Number.isFinite(voice) ? Math.max(0, Math.min(1, voice)) : 0;
    // LiveKit levels for normal speech sit around 0.05–0.4; lift them into a usable range.
    const target = level > 0.015 ? Math.min(1, Math.sqrt(level) * 1.5) : 0;
    const rate = target > this.mouth ? 28 : 14; // open fast, close a little slower
    this.mouth += (target - this.mouth) * (1 - Math.exp(-rate * dt));
    if (target > 0 && this.elapsed >= this.nextSyllable) {
      let r = this.random(), i = 0;
      while (i < VOWEL_WEIGHTS.length - 1 && (r -= VOWEL_WEIGHTS[i]) > 0) i++;
      this.vowel = i;
      this.nextSyllable = this.elapsed + 0.09 + this.random() * 0.12;
    }
    for (let i = 0; i < VOWELS.length; i++) {
      const want = i === this.vowel ? this.mouth : 0;
      this.vowelWeights[i] += (want - this.vowelWeights[i]) * (1 - Math.exp(-20 * dt));
      if (em && this.hasMouth) em.setValue(VOWELS[i], Math.min(1, this.vowelWeights[i] * (i === 0 ? 1 : 0.85)));
    }

    // --- Eyes ---
    const lookAt = this.vrm.lookAt;
    if (lookAt) {
      if (lookTarget) lookAt.lookAt(lookTarget);
      else { lookAt.yaw += (0 - lookAt.yaw) * Math.min(1, dt * 6); lookAt.pitch += (0 - lookAt.pitch) * Math.min(1, dt * 6); }
    }

    // --- Teleports fling hair: reset springs when the body jumps > 1.5 m ---
    const hips = this.vrm.humanoid.getRawBoneNode('hips');
    if (hips) {
      hips.getWorldPosition(_hips);
      if (_hips.distanceToSquared(this.lastHips) > 2.25) this.needsSpringReset = true;
      this.lastHips.copy(_hips);
    }
    if (this.needsSpringReset) {
      this.vrm.scene.updateMatrixWorld(true);
      this.vrm.springBoneManager?.reset();
      this.needsSpringReset = false;
    }

    // Expressions, look-at, node constraints, spring bones, MToon UV animation.
    // (humanoid.update is a no-op: autoUpdateHumanBones is off.)
    this.vrm.update(dt);
  }
}

/**
 * First-person view of your own VRM: the head (and anything the VRM marks
 * third-person-only) disappears, so the camera doesn't sit inside the face.
 * three-vrm's firstPerson.setup() puts head-erased copies on one layer and the
 * originals on another; we move both back to layer 0 and switch visibility
 * instead, so no camera's layer mask has to change (selfie/mirror cameras keep
 * working, and other people's VRMs are unaffected).
 */
export class VrmFirstPersonView {
  private fpOnly: THREE.Object3D[] = [];
  private tpOnly: THREE.Object3D[] = [];
  private ready = false;
  private on = false;

  constructor(private vrm: VRM) {}

  private setup() {
    if (this.ready || !this.vrm.firstPerson) return;
    const FP = 29, TP = 30;
    this.vrm.firstPerson.setup({ firstPersonOnlyLayer: FP, thirdPersonOnlyLayer: TP });
    this.vrm.scene.traverse((o) => {
      if (o.layers.mask === 1 << FP) this.fpOnly.push(o);
      else if (o.layers.mask === 1 << TP) this.tpOnly.push(o);
    });
    for (const o of [...this.fpOnly, ...this.tpOnly]) o.layers.set(0);
    this.ready = true;
  }

  get firstPerson() { return this.on; }

  setFirstPerson(on: boolean) {
    if (on === this.on && (this.ready || !on)) return;
    if (on) this.setup();
    this.on = on;
    for (const o of this.fpOnly) o.visible = on;
    for (const o of this.tpOnly) o.visible = !on;
  }
}
